/**
 * API utility functions for making server requests
 */
import { getVehicleId } from "./formatting";

/**
 * Captures booking information and sends it to the server
 * @param {Object} appointment - The appointment data
 * @returns {Promise} - Promise with the response data
 */
export const captureBooking = async function (appointment) {
  const captureUrl = `${process.env.REACT_APP_API_URL}/bookings/capture`;

  const capturePayload = {
    domain: appointment?.domain,
    siteId: appointment?.shopData?.shopId,
    fname: appointment?.customer?.fname,
    lname: appointment?.customer?.lname,
    number: appointment?.customer?.phone,
    email: appointment?.customer?.email,
    apptType: appointment?.apptType,
    duration: appointment?.service?.hours,
    title: appointment?.service?.name,
    bailOutStage: appointment?.bailOutStage,
    comments: appointment?.comments,
    date: appointment.date,
    time: appointment.time,
    service: appointment.service,
    newAppointmentId: appointment.bookingId,
    bailed: appointment.bailed,
  };

  try {
    // First fetch call for capture
    const captureResponse = await fetch(captureUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(capturePayload),
    });

    if (!captureResponse.ok) {
      throw new Error(
        "Network response was not ok " + captureResponse.statusText
      );
    }

    const captureData = await captureResponse.json();
    return captureData;
  } catch (error) {
    console.error("Error posting booking config:", error);
  }
};

/**
 * Posts booking configuration to the server
 * @param {Object} appointment - The appointment data
 * @returns {Promise} - Promise with the response data
 */
export const postBookingConfig = async function (appointment) {
  console.log("postBookingConfig", appointment);
  const bookingUrl = `${process.env.REACT_APP_API_URL}/bookings/book?domain=${appointment.domain}&shopId=${appointment.shopData?.shopId}`;

  let rawMessage =
    appointment?.shopData?.messages?.confirmationMessage ||
    "Your appointment has been successfully booked! We look forward to seeing you soon.";
  let message = rawMessage.replace(/{{new_line}}/g, "<br />");

  if (appointment?.service) {
    message = message.replace(
      /{{your_service}}/g,
      "your " + appointment?.service + " service"
    );
  } else {
    message = message.replace(/{{your_service}}/g, "");
  }

  if (appointment?.date) {
    message = message.replace(/{{on_date}}/g, " on " + appointment?.date);
  } else {
    message = message.replace(/{{on_date}}/g, "");
  }

  if (appointment?.time) {
    message = message.replace(/{{at_time}}/g, " at " + appointment?.time);
  } else {
    message = message.replace(/{{at_time}}/g, "");
  }

  message = message.replace(/\s+\?/g, "?");

  try {
    const bookingPayload = {
      apptType: appointment.apptType,
      fname: appointment.customer?.fname,
      lname: appointment.customer?.lname,
      email: appointment.customer?.email,
      number: appointment.customer?.phone,
      phoneNumber: appointment.customer?.phone,

      address1: appointment.customer?.address?.street,
      city: appointment.customer?.address?.city,
      state: appointment.customer?.address?.state,
      zip: appointment.customer?.address?.zip,

      date: appointment.date,
      time: appointment.time,

      year: appointment.vehicle?.year,
      make: appointment.vehicle?.make,
      model: appointment.vehicle?.model,
      vehicleId: getVehicleId(appointment.vehicle),

      integrationId: appointment.customerId,
      dcId: appointment.dcId,
      service: appointment.service,
      hours: appointment.serviceHours,
      comments: appointment.comments,
      serviceComments: "",
      optIn: true,
      siteId: appointment.shopData?.shopId,

      deferredServices: appointment?.deferredServices,

      newAppointmentId: appointment.bookingId,
      rwg_token: appointment.rwg_token,

      confirmationMessage: message,
      services: appointment?.services,
    };

    const bookingResponse = await fetch(bookingUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bookingPayload),
    });

    if (!bookingResponse.ok) {
      throw new Error(
        "Network response was not ok " + bookingResponse.statusText
      );
    }

    await bookingResponse.json();
  } catch (error) {
    console.error("Error posting booking config:", error);
  }
};

/**
 * Looks up customer information by phone number
 * @param {string} customer - Customer phone number
 * @param {Object} config - Config containing domain and shop ID
 * @returns {Promise} - Promise with the customer data
 */
export const customerLookup = async function (customer, config) {
  const url = `${process.env.REACT_APP_API_URL}/bookings/customerLookup?domain=${config.domain}&shopId=${config.shopId}&number=${customer}`;

  try {
    const response = await fetch(url);
    return response.json();
  } catch (error) {
    console.error("Error fetching customer data", error);
  }
};

/**
 * Looks up service information for a customer and vehicle
 * @param {Object} customer - Customer object with _id and vehicle_id
 * @param {Object} config - Config containing domain and shop ID
 * @returns {Promise} - Promise with the service data
 */
export const serviceLookup = async function (customer, config) {
  const url = `${process.env.REACT_APP_API_URL}/bookings/serviceLookup?domain=${config.domain}&shopId=${config.shopId}&cid=${customer._id}&vid=${customer.vehicle_id}`;

  try {
    const response = await fetch(url);
    return response.json();
  } catch (error) {
    console.error("Error fetching customer data", error);
  }
};

/**
 * Gets available booking times for a given date
 * @param {string} date - Date to check availability for
 * @param {Object} appointment - Appointment data containing service
 * @returns {Promise} - Promise with the available times data
 */
export const getBookingTimes = async function (date, appointment) {
  let queryString = "";
  queryString = queryString + `domain=${appointment?.domain}`;
  queryString = queryString + `&shopId=${appointment?.shopData?.shopId}`;
  queryString = queryString + `&date=${date}`;
  queryString = queryString + `&apptType=${appointment?.apptType}`;
  queryString = queryString + `&hours=${appointment?.serviceHours}`;
  queryString =
    queryString + `&service=${encodeURIComponent(appointment?.service)}`;
  queryString =
    queryString + `&services=${encodeURIComponent(appointment?.services)}`;

  const url = encodeURI(
    `${process.env.REACT_APP_API_URL}/bookings/apptOptions?${queryString}`
  );

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    } else {
      return response.json();
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    return { availableSlots: [], unavailableSlots: [] };
  }
};

/**
 * Gets available booking times for a given date (V2 API endpoint)
 * @param {Object} appointment - Appointment data containing service details
 * @returns {Promise} - Promise with the available times data
 */
export const getBookingTimesV2 = async function (appointment) {
  const url = encodeURI(
    `${process.env.REACT_APP_API_URL}/bookings/v2/apptOptions`
  );

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...appointment,
        siteId: appointment.shopId,
        duration: appointment.hours,
        appointment_date: appointment.date,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch data");
    } else {
      return response.json();
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    return { availableSlots: [], unavailableSlots: [] };
  }
};

/**
 * Checks if a specific day has any available appointment slots
 * @param {Date|string} date - The date to check
 * @param {Object} appointment - Appointment data
 * @returns {Promise} - Promise with availability information
 */
export const checkDayAvailability = async function (date, appointment) {
  try {
    const formattedDate =
      date instanceof Date ? date.toISOString().split("T")[0] : date;
    const response = await getBookingTimes(formattedDate, appointment);
    return {
      date: date,
      hasAvailableSlots:
        response.availableSlots && response.availableSlots.length > 0,
    };
  } catch (error) {
    console.error("Error checking day availability:", error);
    return { date: date, hasAvailableSlots: false };
  }
};
